/**
 * The body of a Conv-AI answer, loosely typed on purpose: it is dynamic data
 * discriminated only by `type`, exactly as the backend sends it.
 */
export type ResponseBody = Record<string, any>;

/**
 * The renderable body of an assistant message, whatever envelope it arrived in.
 *
 * POST /query answers `{ response: <body>, thread_id }` (alpha.service.ts has
 * already unwrapped any nested or stringified JSON inside `response`). An
 * envelope carrying its discriminator at the ROOT — `{ type, response: <body> }`
 * — is still accepted, so one body with its own `type` reaches every consumer.
 *
 * Shared by the chat renderer (ChatView) and read-aloud (readAloud.service), so
 * what is spoken is derived from exactly what is shown.
 */
export const resolveResponseBody = (payload: unknown): ResponseBody | null => {
  if (!payload || typeof payload !== 'object') return null;

  const envelope = payload as ResponseBody;
  const inner = envelope.response;

  if (inner && typeof inner === 'object') {
    const body = inner as ResponseBody;
    // Graft the root discriminator on only when the body has none of its own.
    if (!body.type && typeof envelope.type === 'string') {
      return { ...body, type: envelope.type };
    }
    return body;
  }

  // Nothing nested: the payload IS the body.
  return envelope;
};
